import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { POSITION_COLORS, POSITION_BG, MATURITY_BG } from "@/lib/constants";
import { Users, ThumbsUp, AlertTriangle, HelpCircle, FlaskConical, Target, CheckCircle2, X } from "lucide-react";

const MiniList = ({ icon: Icon, title, items, tone }) => (
  <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
    <p className={`font-mono text-[10px] uppercase tracking-widest mb-2 flex items-center gap-1.5 ${tone}`}><Icon className="w-3.5 h-3.5" />{title}</p>
    <ul className="space-y-1.5">
      {items.map((a) => <li key={a} className="text-xs text-slate-700 leading-snug">• {a}</li>)}
    </ul>
  </div>
);

export const VoiceWorkspace = ({ themes, activeTheme, onSelectTheme }) => {
  const [voice, setVoice] = useState(null);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    api.get("/voice").then((r) => setVoice(r.data));
  }, []);

  useEffect(() => {
    if (activeTheme && activeTheme !== "all") {
      api.get(`/themes/${activeTheme}/analysis`).then((r) => setAnalysis(r.data));
    } else {
      setAnalysis(null);
    }
  }, [activeTheme]);

  if (!voice) return null;
  const posTotal = Object.values(voice.position_distribution).reduce((a, b) => a + b, 0) || 1;
  const evTotal = voice.evidence_vs_experience.evidence_based + voice.evidence_vs_experience.experience_based || 1;

  return (
    <div className="space-y-4" data-testid="voice-workspace">
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6" data-testid="voice-synthesis">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-teal-400" />
          <p className="font-mono text-[10px] uppercase tracking-widest text-teal-400">Overall voice synthesis</p>
          <span className="ml-auto font-mono text-xs text-slate-400" title={voice.consensus_calibration?.basis} data-testid="consensus-calibration">Consensus level: <span className="text-white font-bold">{voice.consensus_calibration?.label}</span></span>
        </div>
        {voice.synthesis_points ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5" data-testid="voice-synthesis-points">
            {voice.synthesis_points.map((p) => (
              <div key={p.label} className="flex items-start gap-3">
                <span className="font-mono text-[9px] uppercase tracking-widest text-teal-400 bg-teal-500/10 border border-teal-500/25 rounded px-1.5 py-0.5 mt-0.5 shrink-0 whitespace-nowrap">{p.label}</span>
                <p className="text-sm text-slate-200 leading-snug">{p.text}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-200 leading-relaxed">{voice.synthesis}</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-800">
          <div data-testid="voice-position-dist">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">HCP position distribution</p>
            <div className="flex h-2.5 rounded-full overflow-hidden">
              {Object.entries(voice.position_distribution).map(([k, v]) => (
                <div key={k} style={{ width: `${(v / posTotal) * 100}%`, backgroundColor: POSITION_COLORS[k] }} title={`${k}: ${v}`} />
              ))}
            </div>
            <div className="flex flex-wrap gap-x-2.5 mt-1.5">
              {Object.entries(voice.position_distribution).map(([k, v]) => (
                <span key={k} className="text-[9px] text-slate-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: POSITION_COLORS[k] }} />{k} ({v})
                </span>
              ))}
            </div>
          </div>
          <div data-testid="voice-theme-dist">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Theme distribution</p>
            <div className="space-y-1">
              {voice.theme_distribution.slice(0, 4).map((t) => (
                <div key={t.name} className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-400 truncate flex-1">{t.name}</span>
                  <div className="w-20 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500 rounded-full" style={{ width: `${(t.value / voice.theme_distribution[0].value) * 100}%` }} />
                  </div>
                  <span className="font-mono text-[10px] text-slate-500 w-4 text-right">{t.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div data-testid="voice-evidence-split">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Evidence-supported vs experience-based</p>
            <div className="flex h-2.5 rounded-full overflow-hidden">
              <div className="bg-indigo-500" style={{ width: `${(voice.evidence_vs_experience.evidence_based / evTotal) * 100}%` }} />
              <div className="bg-amber-500" style={{ width: `${(voice.evidence_vs_experience.experience_based / evTotal) * 100}%` }} />
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">
              <span className="text-indigo-400 font-semibold">{voice.evidence_vs_experience.evidence_based} evidence-anchored</span> ·{" "}
              <span className="text-amber-400 font-semibold">{voice.evidence_vs_experience.experience_based} experience-based</span>
            </p>
            <p className="text-[9px] text-slate-500 mt-0.5">{voice.evidence_vs_experience.note}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <MiniList icon={ThumbsUp} title="Key supporting arguments" items={voice.key_arguments} tone="text-emerald-700" />
        <MiniList icon={AlertTriangle} title="Key concerns" items={voice.key_concerns} tone="text-red-700" />
        <MiniList icon={HelpCircle} title="Unanswered clinical questions" items={voice.unanswered_questions} tone="text-amber-700" />
      </div>

      <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="theme-selector">
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Select a clinical theme for deep analysis</p>
        <div className="flex flex-wrap gap-2">
          {themes.map((t) => (
            <button
              key={t.id}
              data-testid={`workspace-theme-${t.id}`}
              onClick={() => onSelectTheme(activeTheme === t.id ? "all" : t.id)}
              className={`text-xs font-semibold rounded-full border px-3 py-1.5 transition-colors ${
                activeTheme === t.id ? "bg-slate-900 text-teal-300 border-slate-900" : "bg-slate-50 text-slate-600 border-slate-200 hover:border-teal-400"
              }`}
            >
              {t.name}
            </button>
          ))}
        </div>
      </div>

      {analysis && (
        <div className="bg-white border border-teal-300 ring-1 ring-teal-100 rounded-xl p-6 shadow-sm" data-testid="theme-analysis-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                <Badge variant="outline" className={`text-[10px] ${MATURITY_BG[analysis.maturity]}`}>{analysis.maturity}</Badge>
                <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">Confidence {analysis.analysis.confidence}/100</Badge>
              </div>
              <h3 className="font-heading text-base font-bold text-slate-900">{analysis.name}</h3>
            </div>
            <button data-testid="theme-analysis-close" onClick={() => onSelectTheme("all")} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed mt-2 border-l-2 border-teal-500 pl-3">{analysis.analysis.position_synthesis}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="bg-emerald-50/50 border border-emerald-100 rounded-lg p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-700 mb-1.5 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Supporting evidence</p>
              <ul className="space-y-1">{analysis.analysis.supporting_evidence.map((e) => <li key={e} className="text-xs text-slate-700">• {e}</li>)}</ul>
            </div>
            <div className="bg-red-50/50 border border-red-100 rounded-lg p-3">
              <p className="font-mono text-[10px] uppercase tracking-widest text-red-700 mb-1.5 flex items-center gap-1"><FlaskConical className="w-3 h-3" />Contradictory evidence</p>
              <ul className="space-y-1">{analysis.analysis.contradictory_evidence.map((e) => <li key={e} className="text-xs text-slate-700">• {e}</li>)}</ul>
            </div>
          </div>

          <div className="mt-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Representative voices</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
              {analysis.representative_insights.map((r) => (
                <div key={r.id} className="bg-slate-50 border border-slate-200/80 rounded-lg p-3">
                  <p className="text-[11px] text-slate-700 italic leading-snug">"{r.quote.slice(0, 140)}{r.quote.length > 140 ? "…" : ""}"</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[10px] text-slate-500 truncate">{r.attribution?.hcp_name || r.hcp_role} · {r.country}</p>
                    <Badge variant="outline" className={`text-[9px] shrink-0 ${POSITION_BG[r.position]}`}>{r.position}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Segment differences</p>
              <p className="text-xs text-slate-600 leading-snug">{analysis.analysis.segment_differences}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Strategic interpretation</p>
              <p className="text-xs text-slate-700 font-medium leading-snug">{analysis.analysis.strategic_interpretation}</p>
            </div>
          </div>

          <div className="bg-teal-50/60 border border-teal-100 rounded-lg p-3 mt-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-teal-700 mb-1.5 flex items-center gap-1"><Target className="w-3 h-3" />Recommended actions</p>
            <ul className="space-y-1">{analysis.analysis.recommended_actions.map((a) => <li key={a} className="text-xs text-slate-700">• {a}</li>)}</ul>
          </div>
          <p className="text-[10px] text-slate-400 mt-3">Statements below are now filtered to this theme · segment, geographic and academic-vs-community splits reflect the filter.</p>
        </div>
      )}
    </div>
  );
};
